import React from "react";
import MRangeSlider from "../../components/RangeSlider.jsx";
import { Drawer, Stack, Text, Select, Rating, Button, Group, Divider, Box, Loader } from "@mantine/core";
import { DatePickerInput } from "@mantine/dates";
import getLocalizedField from "../../services/getLocalizedField.js";
import { useTrans } from "core";
import TriStateCheckbox from "../../components/TriStateCheckbox.jsx";

export default function ApartmentFilter({ opened, onClose, onApply, initialRange, regions, features }) {
	const [priceRange, setPriceRange] = React.useState([initialRange.min, initialRange.max]);
	const [rate, setRate] = React.useState(0);
	const [regionId, setRegionId] = React.useState(null);
	const [dateRange, setDateRange] = React.useState([null, null]);
	const { t } = useTrans();
	const [featState, setFeatState] = React.useState({});

	React.useEffect(() => {
		const init = {};

		features.forEach((f) => {
			if (f.type === 2) {
				const min = f.minValue ?? 0;
				const max = f.maxValue ?? 10;
				init[f.id] = [min, max];
			} else {
				init[f.id] = null;
			}
		});

		setFeatState(init);
	}, [features]);

	function applyFilters() {
		const formattedFeatures = [];

		Object.entries(featState).forEach(([featureId, value]) => {
			const f = features.find((x) => x.id == featureId);
			if (!f) return;

			// TYPE 1 → tri-state checkbox → true, false or null
			if (f.type === 1) {
				if (value !== null && value !== undefined) {
					formattedFeatures.push({
						featureId: Number(featureId),
						value: value, // true/false
					});
				}
			}

			// TYPE 2 → range slider → { minValue, maxValue }
			if (f.type === 2) {
				formattedFeatures.push({
					featureId: Number(featureId),
					value: {
						minValue: value[0],
						maxValue: value[1],
					},
				});
			}
		});

		onApply({
			priceRange,
			rate,
			regionId,
			startDate: dateRange[0],
			endDate: dateRange[1],
			features: formattedFeatures,
		});

		onClose();
	}

	function resetFilters() {
		setPriceRange([initialRange.min, initialRange.max]);
		setRate(0);
		setRegionId(null);
		setDateRange([null, null]);
		setFeatState({});
		onApply(null);
		onClose();
	}

	function renderFeature(f) {
		const label = getLocalizedField(f, "name");

		if (f.type === 1) {
			return (
				<Box key={f.id} dir="rtl">
					<Text size="sm">{label}</Text>
					<TriStateCheckbox
						value={featState[f.id]}
						onChange={(next) =>
							setFeatState((prev) => ({
								...prev,
								[f.id]: next,
							}))
						}
					/>
				</Box>
			);
		}

		if (f.type === 2) {
			return (
				<Box key={f.id} dir="ltr" mt="md">
					<Text size="sm">{label}</Text>

					<MRangeSlider
						min={f.minValue ?? 0}
						max={f.maxValue ?? 10}
						value={featState[f.id]}
						onChange={(v) =>
							setFeatState((prev) => ({
								...prev,
								[f.id]: v,
							}))
						}
					/>

					<Group justify="space-between" mt={4}>
						<Text size="xs">{featState[f.id]?.[0]}</Text>
						<Text size="xs">{featState[f.id]?.[1]}</Text>
					</Group>
				</Box>
			);
		}
	}

	return (
		<Drawer opened={opened} onClose={onClose} title={t("apartment.filters")} dir="ltr" size="md">
			{" "}
			<Stack spacing="lg" pb={70}>
				{/* Rating */}{" "}
				<Box>
					{" "}
					<Text>{t("baseApartmentDetails.rate")}</Text> <Rating value={rate} onChange={setRate} size="lg" />{" "}
				</Box>
				{/* Price */}
				<Box>
					<Group justify="space-between">
						<Text size="xs">
							{priceRange[0]} - {priceRange[1]}
						</Text>
					</Group>

					<MRangeSlider min={initialRange.min} max={initialRange.max} value={priceRange} onChange={setPriceRange} />
				</Box>
				{/* Region */}
				<div dir="rtl">
					<Select
						label={t("region.chooseCountry")}
						data={regions.map((r) => ({
							value: String(r.id),
							label: getLocalizedField(r, "name"),
						}))}
						clearable
						value={regionId}
						onChange={setRegionId}
					/>
				</div>
				{/* Dates */}
				<div dir="rtl">
					<DatePickerInput
						type="range"
						label={t("apartment.BookingDates")}
						value={dateRange}
						onChange={setDateRange}
						clearable
					/>
				</div>
				{/* Features */}
				<Divider label={t("features.features")} />
				{features.length === 0 ? <Loader size="sm" /> : features.map((f) => renderFeature(f))}
			</Stack>
			{/* FOOTER */}
			<Box pos="fixed" right={0} p="md" w="440px" bg="white" style={{ borderTop: "1px solid #ddd" }}>
				<Group grow>
					<Button variant="outline" onClick={resetFilters}>
						{t("baseApartmentDetails.reset")}
					</Button>
					<Button onClick={applyFilters}>{t("baseApartmentDetails.filters")}</Button>
				</Group>
			</Box>
		</Drawer>
	);
}
